import { translations } from '@tools/translations/translations';
import { isArray } from 'lodash-es';
import { Logger } from '@tools/logger/logger';
import { TwigFormRules } from './rules';

export class TwigFormValidator {
  formSelector;

  rules;

  messages;

  onSuccess;

  onFailure;

  onSubmit;

  errors = {};

  /**
   * @param options
   */
  constructor(options) {
    this.formSelector = options.formSelector;
    this.rules = options.rules;
    this.messages = options.messages;
    this.onSuccess = options.onSuccess;
    this.onFailure = options.onFailure;
    this.onSubmit = options.onSubmit;
  }

  init() {
    const form = document.querySelector(this.formSelector);
    form.setAttribute('novalidate', '');
    form.addEventListener('submit', (event) => {
      if (this.onSubmit && this.onSubmit(event)) {
        return true;
      }

      if (!this.validate()) {
        event.preventDefault();
        event.stopPropagation();
        this.focusFirstError();
        if (this.onFailure) {
          return this.onFailure(event, this.errors);
        }
        return false;
      }
      this.avoidMultipleSubmit();
      if (this.onSuccess) {
        return this.onSuccess(event);
      }
      return true;
    });
    form.setAttribute('data-ready', true);
    Logger.debug(`TwigFormValidator initialized on ${this.formSelector}`);
  }

  validate() {
    Logger.debug(`TwigFormValidator running validation...`);
    this.errors = {};
    let success = true;
    Object.keys(this.rules).forEach((fieldName) => {
      success = this.validateField(fieldName) && success;
    });
    return success;
  }

  validateField(fieldName) {
    const form = document.querySelector(this.formSelector);
    const input = form.querySelector(`[name="${fieldName}"]`);
    let rules = this.rules[fieldName];
    if (!isArray(rules)) {
      rules = [ rules ];
    }
    let messages = this.messages ? this.messages[fieldName] : [];
    if (!isArray(messages)) {
      messages = [ messages ];
    }
    let success = true;
    rules.forEach((rule, index) => {
      if (!success) {
        return;
      }
      success = this.validateInputForRule(
        input,
        rule,
        messages[index],
      );
    });
    Logger.debug(`TwigFormValidator ${success ? 'succeed' : 'failed'} on ${fieldName}`);
    return success;
  }

  validateInputForRule(input, rule, message) {
    if (!input) {
      return true;
    }
    this.resetInputError(input);
    let defaultMessage = 'invalid';
    let callableRule = rule;
    if (typeof rule === 'string') {
      defaultMessage = rule;
      callableRule = TwigFormRules[rule];
    }
    try {
      if (!callableRule(input.value, input)) {
        this.showInputError(input, message || defaultMessage);
        return false;
      }
    } catch (err) {
      Logger.error(err);
      this.showInputError(input, defaultMessage);
      return false;
    }
    return true;
  }

  resetInputError(input) { // eslint-disable-line class-methods-use-this
    const parent = input.parentNode;
    parent.querySelector('label.error:not(.in-vue-component)')?.remove();
    input.classList.remove('error');
    parent.classList.remove('error');
  }

  showInputError(input, message) {
    const translatedMessage = this.getTranslatedMessage(message);
    if (!translatedMessage) {
      return;
    }
    this.errors[input.name] = translatedMessage;
    if (input.type === 'hidden' || input.type === 'radio') {
      return;
    }
    const parent = input.parentNode;
    parent.insertAdjacentHTML('beforeend', `<label class="error">${translatedMessage}</label>`);
    input.classList.add('error');
    if (parent.classList.contains('input-select')) {
      parent.classList.add('error');
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getTranslatedMessage(message) {
    switch (typeof message) {
      case 'string':
        return translations.validators[`form.twig.validator.${message}`] || message;
      case 'function':
        return message();
      default:
        return message;
    }
  }

  focusFirstError() {
    document.querySelector(`${this.formSelector} .error`)?.scrollIntoView(
      { behavior: 'smooth', block: 'center' },
    );
  }

  avoidMultipleSubmit() {
    document.querySelectorAll(`${this.formSelector} button[type=submit]`).forEach((button) => {
      // return false instead of disabled attribute because button may have data to submit
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        return false;
      });
    });
  }
}
